import api from './api';

/**
 * Öffentliches Hilfecenter (/hilfe) — Block-basierte Tutorials & Anleitungen.
 * Endpunkte:
 *   GET /api/help/overview?q=      → { categories, articles } (ohne Blöcke)
 *   GET /api/help/article/:slug    → kompletter Artikel (nur veröffentlicht)
 */

export type HelpBlockType =
  | 'heading' | 'paragraph' | 'image' | 'gallery' | 'table' | 'list' | 'callout' | 'divider';

export interface HelpGalleryImage {
  url: string;
  caption: string;
}

export interface HelpBlock {
  blockId: string;
  type: HelpBlockType;
  text: string;
  level: number;
  imageUrl: string;
  imageAlt: string;
  imageCaption: string;
  imageMaxWidth: number | null;
  images: HelpGalleryImage[];
  tableHeaders: string[];
  tableRows: string[][];
  listItems: string[];
  listType: 'bullet' | 'numbered';
  calloutVariant: 'info' | 'tip' | 'warning';
  order: number;
}

export interface HelpCategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  sortOrder: number;
  visible: boolean;
  /** Zielgruppe (22.10.2026): Slug aus dem Backend-Katalog; null = Standard (Turniersoftware) */
  audience: string | null;
}

/** Zielgruppe „Was bist du?“ – erste Ebene des Hilfecenters (22.10.2026) */
export interface HelpAudience {
  slug: string;
  name: string;
  short: string;
  description: string;
  icon: string;
  sortOrder: number;
}

export const DEFAULT_HELP_AUDIENCE = 'veranstalter';

export interface HelpArticleSummary {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  categoryId: string | null;
  headerImage: string | null;
  sortOrder: number;
  updatedAt: number | null;
  /** zusätzliche Zielgruppen, in denen der Artikel auch erscheint (22.10.2026) */
  audiences: string[];
}

export interface HelpOverview { categories: HelpCategory[]; articles: HelpArticleSummary[]; audiences: HelpAudience[]; defaultAudience: string }

export interface HelpArticle extends HelpArticleSummary {
  blocks: HelpBlock[];
  views: number;
}

export const parseHelpBlock = (b: any): HelpBlock => ({
  blockId: b?.blockId ?? '',
  type: (b?.type ?? 'paragraph') as HelpBlockType,
  text: b?.text ?? '',
  level: b?.level === 3 ? 3 : 2,
  imageUrl: b?.imageUrl ?? '',
  imageAlt: b?.imageAlt ?? '',
  imageCaption: b?.imageCaption ?? '',
  imageMaxWidth: typeof b?.imageMaxWidth === 'number' ? b.imageMaxWidth : null,
  images: Array.isArray(b?.images)
    ? b.images
        .map((img: any) => ({ url: img?.url ?? '', caption: img?.caption ?? '' }))
        .filter((img: HelpGalleryImage) => img.url.length > 0)
    : [],
  tableHeaders: Array.isArray(b?.tableHeaders) ? b.tableHeaders.map((h: any) => String(h ?? '')) : [],
  tableRows: Array.isArray(b?.tableRows)
    ? b.tableRows
        .filter((r: any) => Array.isArray(r))
        .map((r: any[]) => r.map((c) => String(c ?? '')))
    : [],
  listItems: Array.isArray(b?.listItems) ? b.listItems.filter((x: any) => typeof x === 'string') : [],
  listType: b?.listType === 'numbered' ? 'numbered' : 'bullet',
  calloutVariant: ['info', 'tip', 'warning'].includes(b?.calloutVariant) ? b.calloutVariant : 'info',
  order: b?.order ?? 0,
});

export const parseHelpCategory = (d: any): HelpCategory | null => {
  if (!d || typeof d !== 'object' || !d.id) return null;
  return {
    id: d.id,
    slug: d.slug ?? d.id,
    name: d.name ?? '',
    description: d.description ?? null,
    icon: d.icon ?? null,
    sortOrder: d.sortOrder ?? 0,
    visible: d.visible !== false,
    audience: typeof d.audience === 'string' && d.audience ? d.audience : null,
  };
};

export const parseHelpAudience = (d: any): HelpAudience | null => {
  if (!d || typeof d !== 'object' || typeof d.slug !== 'string' || !d.slug) return null;
  return { slug: d.slug, name: d.name ?? d.slug, short: d.short ?? d.name ?? d.slug, description: d.description ?? '', icon: d.icon ?? 'life-buoy', sortOrder: d.sortOrder ?? 0 };
};

export const parseHelpArticleSummary = (d: any): HelpArticleSummary | null => {
  if (!d || typeof d !== 'object' || !d.id) return null;
  return {
    id: d.id,
    slug: d.slug ?? d.id,
    title: d.title ?? '',
    description: d.description ?? null,
    categoryId: d.categoryId ?? null,
    headerImage: d.headerImage ?? null,
    sortOrder: d.sortOrder ?? 0,
    updatedAt: d.updatedAt ?? null,
    audiences: Array.isArray(d.audiences) ? d.audiences.filter((a: unknown): a is string => typeof a === 'string' && a.length > 0) : [],
  };
};

export const parseHelpArticle = (d: any): HelpArticle | null => {
  const summary = parseHelpArticleSummary(d);
  if (!summary) return null;
  return {
    ...summary,
    blocks: Array.isArray(d.blocks)
      ? d.blocks.map(parseHelpBlock).sort((a: HelpBlock, b: HelpBlock) => a.order - b.order)
      : [],
    views: d.views ?? 0,
  };
};

class HelpCenterService {
  async getOverview(q?: string): Promise<HelpOverview> {
    const res = await api.get('/api/help/overview', { params: q?.trim() ? { q: q.trim() } : {} });
    const categories = (Array.isArray(res.data?.categories) ? res.data.categories : [])
      .map(parseHelpCategory)
      .filter(Boolean) as HelpCategory[];
    const articles = (Array.isArray(res.data?.articles) ? res.data.articles : [])
      .map(parseHelpArticleSummary)
      .filter(Boolean) as HelpArticleSummary[];
    // Zielgruppen (22.10.2026): kommen vom Backend; ältere Backends liefern sie nicht → leere Liste, Startseite fällt auf Kategorien zurück
    const audiences = (Array.isArray(res.data?.audiences) ? res.data.audiences : []).map(parseHelpAudience).filter(Boolean) as HelpAudience[];
    const defaultAudience = typeof res.data?.defaultAudience === 'string' && res.data.defaultAudience ? res.data.defaultAudience : DEFAULT_HELP_AUDIENCE;
    return { categories, articles, audiences: audiences.sort((a, b) => a.sortOrder - b.sortOrder), defaultAudience };
  }

  async getArticleBySlug(slug: string): Promise<HelpArticle | null> {
    try {
      const res = await api.get(`/api/help/article/${encodeURIComponent(slug)}`);
      return parseHelpArticle(res.data);
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  buildFileUrl(path?: string | null): string | null {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const withApi = path.startsWith('/api/') ? path : `/api/file/${path}`;
    return `${api.defaults.baseURL}${withApi}`;
  }
}

const helpCenterService = new HelpCenterService();
export default helpCenterService;
